import React, { useMemo, useState, useRef } from 'react';
import { View, ScrollView, StyleSheet, useWindowDimensions, TouchableOpacity, PanResponder } from 'react-native';
import { useTheme } from '@react-navigation/native';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomDropDown from '../../../components/CustomDropDown/CustomDropdown';
import { useTranslation } from 'react-i18next';
import Ionicons from "@react-native-vector-icons/ionicons/static";
import { formatDateForUI } from '../../../constants/FormatDate';
import CustomDropDownWithIcons from '../../../components/CustomDropDownWithIcons';
import Modal from "react-native-modal";

const TSExpenseTable = ({
    data,
    entityOptions,
    jobCodeOptions,
    expenseOptions,
    onEdit,
    onDelete,
    toView = false,
    openCalendar,
    selectedOptions,
    setSelectedOptions
}) => {
    const { t } = useTranslation();
    const { width: screenWidth } = useWindowDimensions();
    const { colors } = useTheme();
    const styles = makeStyles(colors);

    const [modalVisible, setModalVisible] = useState(false);
    const [modalSelectedValues, setModalSelectedValues] = useState('');
    const [activeRowIndex, setActiveRowIndex] = useState(null);
    const [activeRowItem, setActiveRowItem] = useState(null);

    // Fixed widths for columns
    const availableWidth = screenWidth * 0.8;
    const initialWidths = {
        entity: availableWidth * 0.1,
        jobName: availableWidth * 0.15,
        date: availableWidth * 0.15,
        expense: availableWidth * 0.15,
        description: availableWidth * 0.1,
        unitRate: availableWidth * 0.1,
        measureUnit: availableWidth * 0.1,
        units: availableWidth * 0.1,
        total: availableWidth * 0.05,
    };

    const [colWidths, setColWidths] = useState(initialWidths);

    const openFilterModal = (index, item,) => {
        setActiveRowIndex(index);
        setActiveRowItem(item);
        setModalVisible(true);
    };

    const ExpenseDropdownOptions = useMemo(() =>
        expenseOptions.map(({ id, description }) => ({
            value: id,
            label: description
        })), [expenseOptions]);

    // Calculate totals
    const calculateTotal = (item) => {
        const rate = item.rateofunit || 0;
        const units = item.units || 0;
        const total = rate * units;
        return parseFloat(total.toFixed(2));
    };

    // Ref to track which column is resizing
    const resizingCol = useRef(null);

    // Create PanResponder for resizing columns
    const createPanResponder = (colKey) => 
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onPanResponderGrant: () => {
                resizingCol.current = colKey;
            },
            onPanResponderMove: (evt, gestureState) => {
                if (!resizingCol.current) return;
                const deltaX = gestureState.dx;
                setColWidths((prev) => {
                    const colKeys = Object.keys(prev);
                    const index = colKeys.indexOf(resizingCol.current);
                    if (index === -1) return prev;

                    const minWidth = 50;
                    const totalWidth = availableWidth;

                    // Current widths copy
                    const newWidths = { ...prev };

                    // Proposed new width for resized column
                    let newWidth = prev[resizingCol.current] + deltaX;
                    if (newWidth < minWidth) newWidth = minWidth;

                    // Calculate width difference
                    let widthDiff = newWidth - prev[resizingCol.current];

                    // Adjust resized column
                    newWidths[resizingCol.current] = newWidth;

                    // Adjust next column to keep total width constant
                    // Find next column to shrink
                    let nextIndex = index + 1;
                    while (nextIndex < colKeys.length) {
                        const nextCol = colKeys[nextIndex];
                        const nextColWidth = newWidths[nextCol];
                        const nextColNewWidth = nextColWidth - widthDiff;

                        if (nextColNewWidth >= minWidth) {
                            newWidths[nextCol] = nextColNewWidth;
                            break;
                        } else {
                            // If next column can't shrink enough, set to minWidth and continue to next
                            const diffUsed = nextColWidth - minWidth;
                            newWidths[nextCol] = minWidth;
                            // Reduce widthDiff by diffUsed and continue
                            widthDiff -= diffUsed;
                            nextIndex++;
                        }
                    }

                    // If no next column can shrink enough, try previous columns or limit resize
                    // For simplicity, limit resize here:
                    if (nextIndex === colKeys.length) {
                        // Can't resize further, revert to previous widths
                        return prev;
                    }

                    return newWidths;
                });
            },
            onPanResponderRelease: () => {
                resizingCol.current = null;
            },
        });

    // Create PanResponders for each column that you want resizable
    const panResponders = {
        entity: createPanResponder('entity'),
        jobName: createPanResponder('jobName'),
        date: createPanResponder('date'),
        expense: createPanResponder('expense'),
        description: createPanResponder('description'),
        unitRate: createPanResponder('unitRate'),
        measureUnit: createPanResponder('measureUnit'),
        units: createPanResponder('units'),
        total: createPanResponder('total'),
    };

    return (
        <View style={styles.container}>
            {/* Header Row */}
            <View style={[styles.row, styles.headerRow]}>
                <View style={[styles.cell_dropdown, { width: colWidths.entity, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:entity')} type="MAJOR" />
                    <View
                        {...panResponders.entity.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.jobName, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:jobName')} type="MAJOR" />
                    <View
                        {...panResponders.jobName.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.date, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:date')} type="MAJOR" />
                    <View
                        {...panResponders.date.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.expense, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:expense')} type="MAJOR" />
                    <View
                        {...panResponders.expense.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.description, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:description')} type="MAJOR" />
                    <View
                        {...panResponders.description.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.unitRate, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:unitRate')} type="MAJOR" />
                    <View
                        {...panResponders.unitRate.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.measureUnit, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:measureUnit')} type="MAJOR" />
                    <View
                        {...panResponders.measureUnit.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_dropdown, { width: colWidths.units, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:units')} type="MAJOR" />
                    <View
                        {...panResponders.units.panHandlers}
                        style={styles.resizer}
                    />
                </View>
                <View style={[styles.cell_last, { width: colWidths.total, alignItems: 'center' }]}>
                    <CustomLabel value={t('common:total')} type="MAJOR" />
                </View>
            </View>

            <ScrollView style={{ maxHeight: 550 }}>
                {data.map((item, index) => (
                    <View key={index} style={styles.row}>
                        {/* Entity dropdown */}
                        <View style={[styles.cell_dropdown, { width: colWidths.entity, alignItems: 'center', backgroundColor: '#f2f2f2' }]}>
                            <CustomDropDown
                                disabled={toView}
                                defValue={item.company}
                                options={toView ? [{ value: item.company, label: (entityOptions.find(option => option.value === item.company)?.label ?? '') }] : entityOptions}
                                value={item.company}
                                setValue={(val) => onEdit(index, item, 'company', val)}
                                type='RESULTS'
                            />
                        </View>
                        {/* JobName dropdown */}
                        <View style={[styles.cell_dropdown, { width: colWidths.jobName, alignItems: 'center' }]}>
                            <CustomDropDown
                                disabled={toView}
                                defValue={item.jobno}
                                options={
                                    toView ? [{ value: item.jobno, label: item.jobno }] :
                                        jobCodeOptions.filter(opt => selectedOptions.jobno.includes(opt.value))
                                }
                                value={item.jobno}
                                setValue={(val) => onEdit(index, item, 'jobno', val)}
                                type='RESULTS'
                            />
                            {!toView && (
                                <TouchableOpacity onPress={() => openFilterModal(index, item)} style={{ marginLeft: 5 }}>
                                    <Ionicons name="search-outline" size={20} color={colors.text} />
                                </TouchableOpacity>
                            )}
                        </View>
                        {/* Date calendar picker */}
                        <View style={[styles.cell, { width: colWidths.date, alignItems: 'center', flexDirection: 'row', backgroundColor: '#f2f2f2' }]}>
                            <CustomInput
                                value={formatDateForUI(item.doe)}
                                maxLen={10}
                                editable={false}
                                type="TIMESHEETS"
                            />
                            <Ionicons
                                name={"calendar-outline"}
                                style={styles.icons}
                                onPress={() => openCalendar(index, item.doe)}
                            />
                        </View>
                        {/* Expense dropdown */}
                        <View style={[styles.cell_dropdown, { width: colWidths.expense, alignItems: 'center' }]}>
                            <CustomDropDown
                                disabled={toView}
                                defValue={item.expcode}
                                options={toView ? [{ value: item.expcode, label: (ExpenseDropdownOptions.find(option => option.value === item.expcode)?.label ?? '') }] : ExpenseDropdownOptions}
                                value={item.expcode}
                                setValue={(val) => {
                                    // Find the selected expense object from expenseOptions
                                    const selectedExpense = expenseOptions.find(exp => exp.id === val);
                                    if (selectedExpense) {
                                        // Update expcode
                                        onEdit(index, item, 'expcode', val);
                                        // Update rateofunit based on selected expense
                                        onEdit(index, item, 'rateofunit', selectedExpense.rateofunit);
                                        // Update unitofmeasure based on selected expense
                                        onEdit(index, item, 'unitofmeasure', selectedExpense.unit);
                                    }
                                }}
                                type='RESULTS'
                            />
                        </View>
                        {/* Description input */}
                        <View style={[styles.cell, { width: colWidths.description, alignItems: 'center', backgroundColor: '#f2f2f2' }]}>
                            <CustomInput
                                editable={!toView}
                                value={item.expdescr}
                                setValue={(val) => onEdit(index, item, 'expdescr', val)}
                                maxLen={150}
                                type="TIMESHEETS"
                            />
                        </View>
                        {/* UnitRate label */}
                        <View style={[styles.cell, { width: colWidths.unitRate, alignItems: 'center' }]}>
                            <CustomInput
                                editable={false}
                                value={item.rateofunit}
                                maxLen={30}
                                type="TIMESHEETS"
                            />
                        </View>
                        {/* Measure Unit label */}
                        <View style={[styles.cell, { width: colWidths.measureUnit, alignItems: 'center', backgroundColor: '#f2f2f2' }]}>
                            <CustomInput
                                editable={false}
                                value={item.unitofmeasure}
                                maxLen={30}
                                type="TIMESHEETS"
                            />
                        </View>
                        {/* Units numeric input */}
                        <View style={[styles.cell, { width: colWidths.units, alignItems: 'center' }]}>
                            <CustomInput
                                editable={!toView}
                                value={item.units?.toString() ?? ''}
                                setValue={(val) => onEdit(index, item, 'units', val.replace(/[^0-9.]/g, ''))}
                                maxLen={10}
                                keyboardType="numeric"
                                type="TIMESHEETS"
                            />
                        </View>
                        {/* Total label */}
                        <View style={[styles.cell_last, {
                            width: colWidths.total,
                            alignItems: 'center',
                            backgroundColor: colors.background_card,
                            flexDirection: 'row',
                        }]}>
                            <CustomLabel value={calculateTotal(item)} type="MAJOR" />
                            {!toView && onDelete && (
                                <TouchableOpacity onPress={() => onDelete(item, index, 'rowguid')} style={{ marginLeft: 8 }}>
                                    <Ionicons name="trash-outline" size={20} color={colors.text} />
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                ))}
            </ScrollView>
            <Modal
                isVisible={modalVisible}
                hasBackdrop={true}
                backdropColor={colors.background_approved}
                backdropOpacity={0.9}
                onBackdropPress={() => setModalVisible(false)}
                onRequestClose={() => setModalVisible(false)}
            >
                <View style={{ width: '50%', height: '40%', alignSelf: 'center', backgroundColor: 'white', padding: 20, borderRadius: 10, zIndex: 10000 }}>
                    <View style={{ alignSelf: 'center', margin: 20 }}>
                        <CustomLabel value={"All Options"} type="TITLE" />
                    </View>
                    <CustomDropDownWithIcons
                        options={jobCodeOptions}
                        value={modalSelectedValues}
                        defValue={modalSelectedValues}
                        setValue={setModalSelectedValues}
                        type="PARTICIPANT"
                    />
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: 30, zIndex: -1 }}>
                        <CustomButton
                            text={t('common:apply')}
                            onPress={() => {
                                onEdit(activeRowIndex, activeRowItem, 'jobno', modalSelectedValues)
                                setSelectedOptions(prev => {
                                    const nameArr = prev.name || [];
                                    const jobnoArr = prev.jobno || [];
                                    const phasecodeArr = prev.phasecode || [];

                                    return {
                                        ...prev,
                                        name: nameArr,
                                        jobno: [...new Set([...jobnoArr, modalSelectedValues])],
                                        phasecode: phasecodeArr,
                                    };
                                });
                                setModalVisible(false)
                            }}
                            type="BUTTON_SAVE"
                            isGradient={true}
                            sz={15}
                        />
                        <CustomButton
                            text={t('common:cancel')}
                            onPress={() => {
                                setModalSelectedValues('');
                                setModalVisible(false);
                            }}
                            type="BUTTON_CANCEL"
                            sz={15}
                            style={{ marginLeft: 10 }}
                        />
                    </View>
                </View>
            </Modal>
        </View>
    );
};


const makeStyles = (colors) => StyleSheet.create({
    container: {
        flexDirection: 'column',
        borderWidth: 1,
        borderColor: '#ddd',
    },
    row: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ddd',
        alignItems: 'center',
    },
    inrow: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerRow: {
        backgroundColor: '#dcdcdc',
        position: 'relative',
    },
    cell: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%'
    },
    cell_last: {
        padding: 8,
        justifyContent: 'center',
    },
    cell_dropdown: {
        padding: 8,
        borderRightWidth: 1,
        borderRightColor: '#ddd',
        justifyContent: 'center',
        borderRightHeight: '100%',
        flexDirection: 'row',
        paddingTop: 22.5,
        paddingBottom: 22.5
    },
    icons: {
        fontSize: 24,
        marginHorizontal: 10,
    },
    resizer: {
        width: 20,
        height: '100%',
        position: 'absolute',
        right: 0,
        top: 0,
        zIndex: 10,
        borderRightColor: '#666',
        borderRightWidth: 1,
        cursor: 'e-resize',
    },
});

export default TSExpenseTable;